import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { useAuth } from "@spiriidk/auth";
import { FORMATTING_LOCALE_STORAGE_OPTIONS } from "@spiriidk/connect-configs";
import { useLocalStorageSync, type Viewport } from "@spiriidk/utils";
import { useCallback, useMemo } from "react";

import { useEnvironmentInfo } from "../../queries";
import { getBrowserInfo } from "../../utils";

const LABELS = {
  environment: msg`Environment`,
  userId: msg`User ID`,
  browser: msg`Browser`,
  operatingSystem: msg`Operating system`,
  viewportSize: msg`Viewport size`,
  clientBuild: msg`Client build`,
  clientBuildDate: msg`Client build date`,
  serverBuild: msg`Server build`,
  serverBuildDate: msg`Server build date`,
  unknown: msg`Unknown`,
};

interface UseDebugDataChunksParams {
  open: boolean;
  viewport: Viewport;
}

function useDebugDataChunks({ open, viewport }: UseDebugDataChunksParams) {
  const { _ } = useLingui();
  const [locale] = useLocalStorageSync(FORMATTING_LOCALE_STORAGE_OPTIONS);
  const { login } = useAuth();
  const { environmentInfo } = useEnvironmentInfo({ enabled: open });
  const browserInfo = useMemo(() => getBrowserInfo(), []);
  const unknownLabel = useMemo(() => _(LABELS.unknown), [_]);
  const env = environmentInfo?.env || String(import.meta.env.MODE);
  const viewportText = useMemo(() => {
    if (viewport.width <= 0 || viewport.height <= 0) return unknownLabel;

    const numberFormat = new Intl.NumberFormat(locale);
    return `${numberFormat.format(viewport.width)} x ${numberFormat.format(viewport.height)}`;
  }, [viewport, unknownLabel, locale]);
  const browserText = useMemo(
    () =>
      `${browserInfo.name || unknownLabel}${
        browserInfo.version ? ` ${browserInfo.version}` : ""
      }`,
    [browserInfo, unknownLabel]
  );

  const formatBuildDate = useCallback(
    (value: Date | null | undefined) => {
      if (!value) return unknownLabel;

      return new Intl.DateTimeFormat(locale, {
        hour: "numeric",
        minute: "2-digit",
        day: "numeric",
        month: "short",
        year: "numeric",
        hour12: false,
      }).format(value);
    },
    [locale, unknownLabel]
  );

  return useMemo(
    () => [
      [
        { label: _(LABELS.environment), value: env },
        { label: _(LABELS.userId), value: login || unknownLabel },
        { label: _(LABELS.browser), value: browserText },
        {
          label: _(LABELS.operatingSystem),
          value: browserInfo.os || unknownLabel,
        },
        { label: _(LABELS.viewportSize), value: viewportText },
      ],
      [
        {
          label: _(LABELS.clientBuild),
          value: environmentInfo?.clientBuild || unknownLabel,
        },
        {
          label: _(LABELS.clientBuildDate),
          value: formatBuildDate(environmentInfo?.clientBuildDate),
        },
      ],
      [
        {
          label: _(LABELS.serverBuild),
          value: environmentInfo?.serverBuild || unknownLabel,
        },
        {
          label: _(LABELS.serverBuildDate),
          value: formatBuildDate(environmentInfo?.serverBuildDate),
        },
      ],
    ],
    [
      _,
      formatBuildDate,
      env,
      login,
      browserText,
      browserInfo,
      viewportText,
      environmentInfo,
    ]
  );
}

export { useDebugDataChunks };
export type { UseDebugDataChunksParams };
